/**
 * DungeonInviteUI — Animated toast that appears when another player starts a dungeon.
 * Shows host name, theme, level count, a "Join" button, and a 10-second countdown.
 * Appears at the top of the screen with slide-in animation.
 */
export default class DungeonInviteUI {
  constructor() {
    this._el = null;
    this._timerBarEl = null;
    this._countdown = 10;
    this._interval = null;
    this._onAccept = null;
    this._onDecline = null;
    this._accepted = false;
  }

  /**
   * Show the invite with a 10-second countdown.
   * @param {Object} options - { hostName, theme, levelCount }
   * @param {function} onAccept
   * @param {function} onDecline - Called on timeout or dismiss
   */
  show(options, onAccept, onDecline) {
    this._onAccept = onAccept;
    this._onDecline = onDecline;
    this._countdown = 10;
    this._accepted = false;

    this._el = document.createElement('div');
    this._el.className = 'dungeon-invite';

    // Theme display name
    const themeNames = {
      stone_crypt: 'Stone Crypt',
      cave: 'Cave',
      ruins: 'Ruins',
      fire_temple: 'Fire Temple'
    };
    const themeName = themeNames[options.theme] || options.theme;

    this._el.innerHTML = `
      <div class="dungeon-invite-header">
        <span class="dungeon-invite-icon">&#x2694;</span>
        <span class="dungeon-invite-title">Dungeon Invite</span>
      </div>
      <div class="dungeon-invite-body">
        <div class="dungeon-invite-info">
          <span class="dungeon-invite-host">${options.hostName}</span> is entering
          <span class="dungeon-invite-theme">${themeName}</span>
          <span class="dungeon-invite-levels">(${options.levelCount} level${options.levelCount > 1 ? 's' : ''})</span>
        </div>
        <div class="dungeon-invite-actions">
          <button class="dungeon-invite-join">Join</button>
          <button class="dungeon-invite-dismiss">Dismiss</button>
        </div>
      </div>
      <div class="dungeon-invite-timer-track">
        <div class="dungeon-invite-timer-bar"></div>
      </div>
    `;

    document.body.appendChild(this._el);

    this._timerBarEl = this._el.querySelector('.dungeon-invite-timer-bar');

    // Join button
    this._el.querySelector('.dungeon-invite-join').addEventListener('click', () => {
      if (this._accepted) return;
      this._accepted = true;
      this._el.querySelector('.dungeon-invite-join').textContent = 'Joining...';
      this._el.querySelector('.dungeon-invite-join').disabled = true;
      this._el.querySelector('.dungeon-invite-dismiss').style.display = 'none';
      this._onAccept?.();
    });

    // Dismiss button
    this._el.querySelector('.dungeon-invite-dismiss').addEventListener('click', () => {
      this.hide();
      this._onDecline?.();
    });

    // Start countdown
    this._startTime = Date.now();
    this._interval = setInterval(() => this._tick(), 50);

    // Trigger slide-in animation
    requestAnimationFrame(() => {
      this._el.classList.add('dungeon-invite-visible');
    });
  }

  /** Update countdown display. @private */
  _tick() {
    const elapsed = (Date.now() - this._startTime) / 1000;
    const remaining = Math.max(0, 10 - elapsed);

    // Update timer bar
    if (this._timerBarEl) {
      this._timerBarEl.style.width = `${(remaining / 10) * 100}%`;
    }

    // Timeout
    if (remaining <= 0) {
      if (!this._accepted) {
        this.hide();
        this._onDecline?.();
      } else {
        // Already accepted, just stop the timer
        clearInterval(this._interval);
        this._interval = null;
      }
    }
  }

  /** Remove from DOM. */
  hide() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
    if (!this._el) return;

    this._el.classList.remove('dungeon-invite-visible');
    this._el.classList.add('dungeon-invite-hiding');
    const el = this._el;
    this._el = null;
    setTimeout(() => el.remove(), 300);
  }
}
